import { Directive, ElementRef, inject, input, Renderer2, AfterViewInit, OnDestroy } from '@angular/core';

@Directive({
    selector: '[gridKeyboardNavigation]',
    standalone: true,
})
export class GridKeyboardNavigationDirective implements AfterViewInit, OnDestroy {
    elementRef = inject(ElementRef);
    renderer = inject(Renderer2);

    gridElementSelector = input('table');
    rowSelector = input('tr');
    cellSelector = input('th, td');
    focusableSelector = input('a[href], button, input, select, textarea, [tabindex]');

    private gridElement: HTMLElement | null = null;
    private grid: Element[][] = [];
    private readonly cellChildren = new Map<Element, Element[]>();
    private readonly managedElements = new Set<Element>();
    private focusedRow: number = NaN;
    private focusedCol: number = NaN;
    private focusedChildIndex: number = 0;

    private readonly tableMutationObserver: MutationObserver = new MutationObserver(() => {
        if (this.gridElement) {
            this.teardownEventListeners(this.gridElement);
            this.grid = this.initializeGrid(this.gridElement);
            this.restoreFocusPointer();
            this.setupEventListeners(this.gridElement);
        }
    });

    ngAfterViewInit (): void {
        const hostElement: HTMLElement = this.elementRef.nativeElement;
        hostElement.appendChild(this.createStyleElement());

        if (hostElement.tagName === 'TABLE') {
            this.gridElement = hostElement as HTMLElement;
        } else {
            const gridElement = hostElement.querySelector(this.gridElementSelector());
            if (gridElement) {
                this.gridElement = gridElement as HTMLElement;
            }
        }

        if (!this.gridElement) {
            return;
        }

        this.gridElement.classList.add('grid-keyboard-navigation');
        this.gridElement.setAttribute('role', 'grid');

        this.grid = this.initializeGrid(this.gridElement);
        this.restoreFocusPointer();
        this.tableMutationObserver.observe(this.gridElement, {
            childList: true,
            subtree: true,
        });
        this.setupEventListeners(this.gridElement);
    }

    ngOnDestroy (): void {
        this.tableMutationObserver.disconnect();
        if (this.gridElement) {
            this.teardownEventListeners(this.gridElement);
        }
    }

    private setupEventListeners (gridElement: HTMLElement): void {
        gridElement.addEventListener('keydown', this.checkFocusChange);
        gridElement.addEventListener('mouseup', this.focusClickedCell);
        gridElement.addEventListener('focusout', this.handleGridFocusOut);
    }

    private teardownEventListeners (gridElement: HTMLElement): void {
        gridElement.removeEventListener('keydown', this.checkFocusChange);
        gridElement.removeEventListener('mouseup', this.focusClickedCell);
        gridElement.removeEventListener('focusout', this.handleGridFocusOut);
    }

    private createStyleElement (): HTMLStyleElement {
        const gridStyleElement = this.renderer.createElement('style') as HTMLStyleElement;
        const css = `
            .grid-keyboard-navigation-focus {
                outline-offset: -2px;
                outline: var(--p-focus-ring-width) var(--p-focus-ring-style) var(--p-focus-ring-color);
                outline-color: var(--p-focus-ring-color);
            }

            .grid-keyboard-navigation th:focus-visible,
            .grid-keyboard-navigation td:focus-visible,
            .grid-keyboard-navigation th > *:focus-visible,
            .grid-keyboard-navigation td > *:focus-visible {
                outline-offset: -2px;
                outline: var(--p-focus-ring-width) var(--p-focus-ring-style) var(--p-focus-ring-color);
                outline-color: var(--p-focus-ring-color);
            }
        `;

        this.renderer.appendChild(
            gridStyleElement,
            this.renderer.createText(css),
        );

        return gridStyleElement;
    }

    private setFocusPointer (row: number, col: number, childIndex = 0): boolean {
        if (!this.isValidCell(row, col)) {
            return false;
        }

        const previousElement = this.getCurrentFocusTarget();
        if (previousElement && previousElement.isConnected) {
            previousElement.setAttribute('tabindex', '-1');
        }

        this.focusedRow = row;
        this.focusedCol = col;
        this.focusedChildIndex = childIndex;

        const target = this.getCurrentFocusTarget();
        if (target) {
            target.setAttribute('tabindex', '0');
        }

        return true;
    }

    private getCurrentFocusTarget (): Element | null {
        if (!this.isValidCell(this.focusedRow, this.focusedCol)) {
            return null;
        }

        const cell = this.grid[this.focusedRow][this.focusedCol];
        const children = this.cellChildren.get(cell);

        if (!children || children.length === 0) {
            return cell;
        }

        const index = Math.min(this.focusedChildIndex, children.length - 1);
        return children[index] ?? null;
    }

    private restoreFocusPointer (): void {
        if (this.grid.length === 0) {
            this.focusedRow = NaN;
            this.focusedCol = NaN;
            return;
        }

        if (this.isValidCell(this.focusedRow, this.focusedCol)) {
            const target = this.getCurrentFocusTarget();
            if (target) {
                target.setAttribute('tabindex', '0');
            }
            return;
        }

        this.setFocusPointer(0, 0);
    }

    private checkFocusChange = (event: KeyboardEvent): void => {
        if (!event) {
            return;
        }

        this.findFocusedItem(event.target as Element);

        const key = event.key;
        let rowCaret = this.focusedRow;
        let colCaret = this.focusedCol;
        let childCaret = this.focusedChildIndex;

        const currentCell = this.isValidCell(rowCaret, colCaret) ? this.grid[rowCaret][colCaret] : null;
        const currentChildren = currentCell ? this.cellChildren.get(currentCell) ?? [] : [];

        switch (key) {
            case 'ArrowUp':
                rowCaret = this.focusedRow - 1;
                childCaret = 0;
                break;
            case 'ArrowDown':
                rowCaret = this.focusedRow + 1;
                childCaret = 0;
                break;
            case 'ArrowLeft':
                if (childCaret > 0) {
                    childCaret--;
                } else {
                    colCaret = this.focusedCol - 1;
                    childCaret = 0;
                }
                break;
            case 'ArrowRight':
                if (childCaret < currentChildren.length - 1) {
                    childCaret++;
                } else {
                    colCaret = this.focusedCol + 1;
                    childCaret = 0;
                }
                break;
            case 'Home':
                rowCaret = event.ctrlKey ? 0 : this.focusedRow;
                colCaret = 0;
                childCaret = 0;
                break;
            case 'End':
                rowCaret = event.ctrlKey ? this.grid.length - 1 : this.focusedRow;
                colCaret = this.grid[this.focusedRow].length - 1;
                childCaret = 0;
                break;
            default:
                return;
        }

        if (this.isValidCell(rowCaret, colCaret)) {
            this.focusCell(rowCaret, colCaret, childCaret);
        }

        event.preventDefault();
    };

    private findFocusedItem (focusedTarget: Element): void {
        for (let i = 0; i < this.grid.length; i++) {
            for (let j = 0; j < this.grid[i].length; j++) {
                const cell = this.grid[i][j];
                if (cell === focusedTarget || cell.contains(focusedTarget)) {
                    const children = this.cellChildren.get(cell);
                    if (children && children.length > 0) {
                        const childIndex = children.indexOf(focusedTarget);
                        if (childIndex >= 0) {
                            this.setFocusPointer(i, j, childIndex);
                            return;
                        }
                    }

                    this.setFocusPointer(i, j, 0);
                    return;
                }
            }
        }
    }

    private focusCell (row: number, col: number, childIndex = 0): void {
        if (this.setFocusPointer(row, col, childIndex)) {
            const target = this.getCurrentFocusTarget();
            if (target) {
                (target as HTMLElement).focus();
            }
        }
    }

    private handleGridFocusOut = (event: FocusEvent): void => {
        const relatedTarget = event.relatedTarget as Node | null;
        if (relatedTarget && this.gridElement?.contains(relatedTarget)) {
            return;
        }

        // Focus left the grid - reset to the first cell so re-entering via Tab lands there.
        this.setFocusPointer(0, 0);
    };

    private focusClickedCell = (event: MouseEvent): void => {
        const target = event.target as Element | null;
        if (!target) {
            return;
        }

        for (let row = 0; row < this.grid.length; row++) {
            for (let col = 0; col < this.grid[row].length; col++) {
                const cell = this.grid[row][col];
                if (cell === target || cell.contains(target)) {
                    const children = this.cellChildren.get(cell);
                    if (children && children.length > 0) {
                        const childIndex = children.indexOf(target);
                        if (childIndex >= 0) {
                            this.focusCell(row, col, childIndex);
                            return;
                        }
                    }

                    this.focusCell(row, col, 0);
                    return;
                }
            }
        }
    };

    private initializeGrid (gridElement: HTMLElement): Element[][] {
        const grid: Element[][] = [];
        const rows = Array.from(gridElement.querySelectorAll(this.rowSelector()) || []);
        if (rows.length === 0) {
            return [];
        }

        this.cellChildren.clear();
        this.pruneManagedElements();

        for (const row of rows) {
            const rowCells: Element[] = [];
            const cells = Array.from(row.querySelectorAll(this.cellSelector()) || []);

            for (const cell of cells) {
                const focusableChildren = Array.from(cell.querySelectorAll(this.focusableSelector()))
                    .filter(el => this.managedElements.has(el) || (el as HTMLElement).tabIndex >= 0);

                this.cellChildren.set(cell, focusableChildren);

                if (focusableChildren.length > 0) {
                    for (const child of focusableChildren) {
                        this.managedElements.add(child);
                        child.setAttribute('tabindex', '-1');
                        child.addEventListener('focus', this.showKeysIndicator as EventListener);
                        child.addEventListener('blur', this.hideKeysIndicator as EventListener);
                    }
                    rowCells.push(cell);
                } else {
                    cell.setAttribute('tabindex', '-1');
                    cell.addEventListener('focus', this.showKeysIndicator as EventListener);
                    cell.addEventListener('blur', this.hideKeysIndicator as EventListener);
                    rowCells.push(cell);
                }
            }

            grid.push(rowCells);
        }

        return grid;
    }

    private pruneManagedElements (): void {
        for (const element of this.managedElements) {
            if (!element.isConnected) {
                this.managedElements.delete(element);
            }
        }
    }

    private isValidCell (row: number, col: number): boolean {
        return Boolean(
            !isNaN(row) &&
            !isNaN(col) &&
            row >= 0 &&
            col >= 0 &&
            this.grid &&
            this.grid.length &&
            row < this.grid.length &&
            col < this.grid[row].length,
        );
    }

    private showKeysIndicator = (event: FocusEvent): void => {
        const target = event.target as HTMLElement;
        target.classList.add('grid-keyboard-navigation-focus');
    };

    private hideKeysIndicator = (event: FocusEvent): void => {
        const target = event.target as HTMLElement;
        target.classList.remove('grid-keyboard-navigation-focus');
    };
}
